import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import Markdown from "../../components/Markdown";

export default function StudentLessonDetail() {
  const { id } = useParams();
  const [lesson, setLesson] = useState(null);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api.get(`/lessons/${id}`).then(({ data }) => {
      setLesson(data);
      setNotes(data.notes_log || []);
    }).catch(() => {});
  }, [id]);

  if (!lesson) return <div className="text-[#A3A3A3]">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="student-lesson-detail">
      <div>
        <Link to="/student/lessons" className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white" data-testid="back-lessons-link">← Lessons</Link>
        <div className="mt-2">
          <div className={`text-[10px] tracking-[0.3em] uppercase font-bold ${lesson.status === 'cancelled' ? 'text-red-300' : lesson.status === 'completed' ? 'text-emerald-300' : 'text-[#38BDF8]'}`}>
            {lesson.type.replace("_", " ")} · {lesson.status}
          </div>
          <h1 className={`font-display text-5xl font-black uppercase tracking-tighter mt-2 ${lesson.status === 'completed' ? 'line-through decoration-emerald-300/50' : lesson.status === 'cancelled' ? 'line-through decoration-red-300/60' : ''}`}>{lesson.title}</h1>
          <div className="text-sm text-[#A3A3A3] mt-1">
            {new Date(lesson.scheduled_at).toLocaleString()} · {lesson.duration_mins}min · with {lesson.coach_name}
            {lesson.location ? ` · ${lesson.location}` : ""}
          </div>
        </div>
      </div>

      {lesson.status === "completed" && (
        <div className="border border-emerald-400/40 bg-emerald-500/10 p-5" data-testid="student-completed-banner">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold">Completed</div>
          <div className="text-sm mt-2">
            Your coach marked this lesson complete{lesson.completed_at ? ` on ${new Date(lesson.completed_at).toLocaleString()}` : ""}.
          </div>
          <div className="text-xs text-[#A3A3A3] mt-2">Any notes or insights below are your takeaways from this session.</div>
        </div>
      )}

      {lesson.status === "cancelled" && (
        <div className="border border-red-400/40 bg-red-500/10 p-5" data-testid="student-cancelled-banner">
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-300 font-bold">Cancelled by your coach</div>
          {lesson.cancel_reason && (
            <div className="text-sm text-white/90 mt-2"><span className="text-[#A3A3A3] uppercase tracking-widest text-xs">Reason: </span>{lesson.cancel_reason}</div>
          )}
          <div className="text-xs text-[#A3A3A3] mt-2">You can book another open slot anytime from the Book page.</div>
        </div>
      )}

      {lesson.lesson_plan && (
        <div className="card-surface p-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Your plan</div>
          <div className="prose prose-invert max-w-none text-sm mt-4" data-testid="student-lesson-plan">
            <Markdown>{lesson.lesson_plan}</Markdown>
          </div>
        </div>
      )}

      {lesson.insights && (
        <div className="card-surface p-8">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Coach insights</div>
          <div className="prose prose-invert max-w-none text-sm mt-4" data-testid="student-lesson-insights">
            <Markdown>{lesson.insights}</Markdown>
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div className="card-surface p-8" data-testid="student-session-notes">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Session notes</div>
          <h3 className="font-display text-2xl font-black uppercase tracking-tighter mt-2 mb-4">From your coach ({notes.length})</h3>
          <div className="space-y-5">
            {notes.slice().reverse().map((n) => (
              <div key={n.id} className="border-l-2 border-[#38BDF8] pl-4 py-1" data-testid={`student-note-${n.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
                  {new Date(n.created_at).toLocaleString()}
                </div>
                {n.summary ? (
                  <div className="prose prose-invert max-w-none text-sm mt-2">
                    <Markdown>{n.summary}</Markdown>
                  </div>
                ) : (
                  <div className="text-sm text-white/90 mt-2 whitespace-pre-wrap">{n.text}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!lesson.lesson_plan && !lesson.insights && notes.length === 0 && (
        <div className="border border-dashed border-white/10 p-8 text-[#A3A3A3]">
          Your coach hasn't published any plan or notes yet. You'll get a notification when they do.
        </div>
      )}
    </div>
  );
}
